import type { WorldDocument, WorldTransaction } from "@private-fiction/story-core";
import { isSyncBlocked, type SyncFailureState } from "./syncFailure";

export interface WorldDeliveryState extends SyncFailureState {
  schemaRecoveryAt?: string;
  metadataRecoveryAt?: string;
  syncVersion?: number;
  syncTarget?: string;
  deliveryState?: "pending" | "sending";
}

export const WORLD_SYNC_VERSION = 2;
export const WORLD_WRITE_RPC = "apply_story_transaction_v2";

/** A known SQL rejection can be retried once after its missing schema is verified. */
export function canRecoverSessionSchema(item: WorldDeliveryState & { transaction: WorldTransaction }, target: string, fileTypeIds?: readonly string[]): boolean {
  if (item.schemaRecoveryAt || item.syncVersion !== WORLD_SYNC_VERSION || item.syncTarget !== target || item.deliveryState !== "pending"
    || item.lastErrorCode !== "23503" || !item.lastError?.startsWith("unknown File Type: ")) return false;
  const missing = item.lastError.slice("unknown File Type: ".length);
  const required = item.transaction.mutations.flatMap(mutation => mutation.document?.fileTypeId ? [mutation.document.fileTypeId] : []);
  return required.includes(missing) && (!fileTypeIds || required.every(id => fileTypeIds.includes(id)));
}

export function canRecoverLegacyMetadata(item: WorldDeliveryState & { transaction: WorldTransaction }, target: string, fieldKeys?: Record<string, string[]>): boolean {
  if (item.metadataRecoveryAt || item.syncVersion !== WORLD_SYNC_VERSION || item.syncTarget !== target || item.deliveryState !== "pending"
    || item.lastErrorCode !== "22023" || !item.lastError?.startsWith("File Type contains undeclared fields: ")) return false;
  if (!fieldKeys) return true;
  const envelope = new Set(['status','source','images','triggers','templateId','templateVersion','image','imageGeneration']);
  let duplicates = 0;
  for (const mutation of item.transaction.mutations) {
    const doc = mutation.document;
    if (!doc || !doc.fileTypeId || doc.fileTypeId.startsWith('core.')) continue;
    const keys = fieldKeys[doc.fileTypeId];
    if (!Array.isArray(keys) || !keys.every(key => typeof key === 'string')) return false;
    for (const [key, value] of Object.entries(doc.fields)) {
      if (keys.includes(key) || envelope.has(key)) continue;
      if (!['name','title','summary','description'].includes(key) || value !== (key === 'name' || key === 'title' ? doc.title : doc.summary)) return false;
      duplicates++;
    }
  }
  return duplicates > 0;
}

export function worldSyncTarget(projectUrl: string, userId?: string): string | undefined {
  return projectUrl && userId ? `${projectUrl.replace(/\/$/, "")}|${userId}` : undefined;
}

export function worldDeliveryHold(item: WorldDeliveryState, target?: string): string | undefined {
  if (isSyncBlocked(item)) return "รายการนี้หยุดส่งหลังเกิดข้อผิดพลาด เก็บข้อมูลไว้เพื่อตรวจความต่างกับ Cloud";
  if (item.syncVersion !== WORLD_SYNC_VERSION) return "เก็บรายการค้างจากระบบเดิมไว้ก่อน ต้องตรวจข้อมูลกับฐานที่กู้คืนก่อนส่งใหม่";
  if (!target || item.syncTarget !== target) return "รายการนี้ยังไม่ยืนยันปลายทางบัญชีและโปรเจกต์ ต้องตรวจข้อมูลก่อนส่ง";
  if (item.deliveryState === "sending") return "คำขอก่อนหน้ายังไม่ทราบผล เก็บรายการไว้ตรวจการบันทึกก่อนส่งซ้ำ";
  return undefined;
}

/** Only called for a newly created session, never when opening a restored one. */
export function bootstrapTransactions(storyId: string, documents: readonly WorldDocument[], newId = () => crypto.randomUUID()): WorldTransaction[] {
  if (documents.some(document => document.storyId !== storyId || document.revision !== 1 || document.deletedAt)) throw new Error("New-session documents must start at revision 1");
  if (new Set(documents.map(document => document.id)).size !== documents.length) throw new Error("Duplicate bootstrap document");
  const transactions: WorldTransaction[] = [];
  for (let offset = 0; offset < documents.length; offset += 64) {
    transactions.push({
      id: newId(), storyId, actor: "system", createdAt: new Date().toISOString(),
      mutations: documents.slice(offset, offset + 64).map(document => ({
        operation: "create", documentId: document.id, expectedRevision: 0,
        document: structuredClone(document), reason: "Initialize new play session",
      })),
    });
  }
  return transactions;
}
